import React, { useState } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Alert, Grid, Stepper, Step, StepLabel
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import * as XLSX from "sheetjs-style";
import * as XLSXRead from "xlsx";
import {
  parseTeamsPresenzaIF, matchIscrittiPresenza, dedupeTestLMS, mergeReportLezioneCompleto
} from "../../utils/parseReportIF";
import { useAuth } from "../../context/AuthContext";
import { salvaLog } from "../../utils/logger";

function leggiIscrittiXlsx(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSXRead.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        resolve(XLSXRead.utils.sheet_to_json(sheet));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function leggiTestJson(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const dati = JSON.parse(e.target.result);
        resolve(Array.isArray(dati) ? dati : []);
      } catch (err) {
        reject(new Error("Il file JSON non è valido"));
      }
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

const COLONNE = [
  { key: "firstName", label: "First Name", width: 18 },
  { key: "lastName", label: "Last Name", width: 22 },
  { key: "email", label: "Email", width: 38 },
  { key: "presenza", label: "Presenza", width: 16 },
  { key: "risposteCorrette", label: "Risposte corrette", width: 20 },
  { key: "dataEsecuzioneTest", label: "Data esecuzione test", width: 24 },
  { key: "tentativiTest", label: "Tentativi test", width: 16 },
];

export default function ReportIF() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [fileCsv, setFileCsv] = useState(null);
  const [fileIscritti, setFileIscritti] = useState(null);
  const [fileJson, setFileJson] = useState(null);
  const [elaborando, setElaborando] = useState(false);
  const [errore, setErrore] = useState(null);
  const [risultato, setRisultato] = useState(null);

  const tuttiFilePresenti = fileCsv && fileIscritti && fileJson;

  async function generaReport() {
    setElaborando(true);
    setErrore(null);
    setRisultato(null);
    try {
      const [{ partecipanti }, iscritti, testRecord] = await Promise.all([
        parseTeamsPresenzaIF(fileCsv),
        leggiIscrittiXlsx(fileIscritti),
        leggiTestJson(fileJson),
      ]);

      const arricchiti = matchIscrittiPresenza(partecipanti, iscritti);
      const testDedup = dedupeTestLMS(testRecord);
      const { rows, summary } = mergeReportLezioneCompleto(iscritti, arricchiti, testDedup);

      setRisultato({ rows, summary });
      salvaLog(currentUser, "Generazione Report IF", "", fileCsv?.name || "", [
        { campo: "Iscritti totali", da: "—", a: String(summary.iscrittiTotali) },
        { campo: "Presenti", da: "—", a: String(summary.presenti) },
        { campo: "Test completati", da: "—", a: String(summary.testCompletati) },
      ]);
    } catch (err) {
      console.error("Errore generazione report IF:", err);
      setErrore(err.message || "Errore durante l'elaborazione dei file. Verifica i formati caricati.");
    }
    setElaborando(false);
  }

  function esportaExcel() {
    if (!risultato) return;
    const wb = XLSX.utils.book_new();
    const ws = {};

    const headerFont = { name: "Arial", bold: true, color: { rgb: "FFFFFF" }, sz: 11 };
    const border = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    COLONNE.forEach((c, cIdx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: cIdx });
      ws[cellRef] = {
        v: c.label,
        t: "s",
        s: {
          fill: { patternType: "solid", fgColor: { rgb: "1F3864" } },
          font: headerFont,
          alignment: { horizontal: "center", vertical: "center", wrapText: true },
          border,
        },
      };
    });

    risultato.rows.forEach((r, rIdx) => {
      const rowFill = rIdx % 2 === 0 ? "FFFFFF" : "EBF0FA";
      COLONNE.forEach((c, cIdx) => {
        const cellRef = XLSX.utils.encode_cell({ r: rIdx + 1, c: cIdx });
        const stile = {
          fill: { patternType: "solid", fgColor: { rgb: rowFill } },
          font: { name: "Arial", sz: 10 },
          alignment: { horizontal: cIdx >= 3 ? "center" : "left", vertical: "center" },
          border,
        };

        if (c.key === "presenza") {
          // Colonna D: valore ora nativo Excel (frazione di giorno), non testo
          if (r.presenzaSecondi != null) {
            ws[cellRef] = { v: r.presenzaSecondi / 86400, t: "n", s: { ...stile, numFmt: "hh:mm:ss" } };
          } else {
            ws[cellRef] = { t: "z", s: stile };
          }
          return;
        }

        const valore = r[c.key];
        if (valore === null || valore === undefined || valore === "") {
          ws[cellRef] = { t: "z", s: stile };
        } else {
          const isNumero = typeof valore === "number";
          ws[cellRef] = { v: valore, t: isNumero ? "n" : "s", s: stile };
        }
      });
    });

    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: risultato.rows.length, c: COLONNE.length - 1 },
    });
    ws["!cols"] = COLONNE.map((c) => ({ wch: c.width }));

    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `Report_IF_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  const step = risultato ? 2 : elaborando ? 1 : 0;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/utility")}
          variant="outlined" size="small">
          Utility
        </Button>
        <Box>
          <Typography variant="h5" fontWeight="bold">Report IF</Typography>
          <Typography variant="body2" color="text.secondary">
            Genera il report lezione completo (presenze + esito test) a partire da 3 file
          </Typography>
        </Box>
      </Box>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step><StepLabel>Carica file</StepLabel></Step>
          <Step><StepLabel>Elaborazione</StepLabel></Step>
          <Step><StepLabel>Report pronto</StepLabel></Step>
        </Stepper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>1. Report presenze Teams (CSV)</Typography>
            <Button variant="outlined" component="label" fullWidth
              startIcon={<UploadFileIcon />} color={fileCsv ? "success" : "primary"}>
              {fileCsv ? fileCsv.name : "Seleziona CSV"}
              <input type="file" accept=".csv" hidden
                onChange={(e) => setFileCsv(e.target.files[0] || null)} />
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Carica qui il report presenze originale scaricato dall'evento Teams.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>2. Iscritti (Excel)</Typography>
            <Button variant="outlined" component="label" fullWidth
              startIcon={<UploadFileIcon />} color={fileIscritti ? "success" : "primary"}>
              {fileIscritti ? fileIscritti.name : "Seleziona XLSX"}
              <input type="file" accept=".xlsx,.xls" hidden
                onChange={(e) => setFileIscritti(e.target.files[0] || null)} />
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Carica qui l'elenco degli iscritti aggiornato ad oggi.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>3. Risultati test LMS (JSON)</Typography>
            <Button variant="outlined" component="label" fullWidth
              startIcon={<UploadFileIcon />} color={fileJson ? "success" : "primary"}>
              {fileJson ? fileJson.name : "Seleziona JSON"}
              <input type="file" accept=".json" hidden
                onChange={(e) => setFileJson(e.target.files[0] || null)} />
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Carica il file JSON dei risultati quiz. Per ottenerlo: esporta il questionario da Gestione quiz
              (arriva via mail in CSV), poi convertilo in JSON su csvjson.com e scaricalo.
            </Typography>
          </Grid>
        </Grid>

        <Box mt={3}>
          <Button variant="contained" disabled={!tuttiFilePresenti || elaborando} onClick={generaReport}
            startIcon={elaborando ? <CircularProgress size={18} color="inherit" /> : null}>
            {elaborando ? "Elaborazione in corso..." : "Genera report"}
          </Button>
        </Box>

        {errore && <Alert severity="error" sx={{ mt: 2 }}>{errore}</Alert>}
      </Paper>

      {risultato && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={4}>
              <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#E6F1FB" }}>
                <Typography variant="h3" fontWeight="bold" color="primary">
                  {risultato.summary.iscrittiTotali}
                </Typography>
                <Typography variant="body2" color="text.secondary">Iscritti totali</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#EAF3DE" }}>
                <Typography variant="h3" fontWeight="bold" color="success.main">
                  {risultato.summary.presenti}
                </Typography>
                <Typography variant="body2" color="text.secondary">Presenti</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#FAEEDA" }}>
                <Typography variant="h3" fontWeight="bold" color="warning.main">
                  {risultato.summary.testCompletati}
                </Typography>
                <Typography variant="body2" color="text.secondary">Test completati</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper elevation={1} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">Anteprima report</Typography>
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={esportaExcel}>
                Esporta Excel
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                    {COLONNE.map((c) => (
                      <TableCell key={c.key}><strong>{c.label}</strong></TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {risultato.rows.map((r, idx) => (
                    <TableRow key={idx} hover>
                      {COLONNE.map((c) => (
                        <TableCell key={c.key}>
                          {r[c.key] !== null && r[c.key] !== undefined && r[c.key] !== "" ? r[c.key] : "—"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
